import React from 'react';
import SectionHeader from '@/components/SectionHeader';
import { SimpleReveal } from '@/hooks/useSimpleAnimation';
import { useHomeData } from '../../hooks/useHomeData';
import { GlowingEffect } from '@/components/ui/glowing-effect';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const CapabilityCard = ({ item }: { item: { icon: React.ReactNode; title: string; description: string } }) => {
  const scrollRef = useScrollReveal<HTMLLIElement>();

  return (
    <li ref={scrollRef} className="h-full list-none scroll-reveal-capability">
      <div className="group relative h-full rounded-2xl border border-neutral-200 p-2 md:p-3 transition-all duration-300 hover:border-[hsl(var(--color-1)/0.45)] hover:shadow-[0_0_20px_4px_hsl(var(--color-1)/0.08)] scroll-reveal-border">
        <GlowingEffect 
          spread={40}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
        />
        <div className="relative flex h-full flex-col overflow-hidden rounded-xl border border-neutral-100 bg-background p-5 md:p-6 shadow-sm transition-all duration-300 group-hover:border-[hsl(var(--color-1)/0.15)] scroll-reveal-inner">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[hsl(var(--color-1))] to-[hsl(var(--color-3))] flex items-center justify-center mb-3 md:mb-4 transition-transform duration-300 group-hover:scale-105 scroll-reveal-icon">
            {React.cloneElement(item.icon as React.ReactElement, { className: 'h-8 w-8 text-white' })}
          </div>
          <h3 className="flex items-start pt-0.5 text-xl leading-[1.375rem] font-semibold font-sans tracking-[-0.04em] md:text-2xl md:leading-[1.875rem] text-balance text-foreground mb-2 md:mb-3">
            {item.title}
          </h3>
          <p className="text-body">
            {item.description}
          </p>
        </div>
      </div>
    </li>
  );
};

export const HomeCapabilitiesSection = () => {
  const { capabilities } = useHomeData();

  return (
    <section className="section-spacing pb-24 md:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-white overflow-visible">
      <SectionHeader title="Development" subtitle="Our Development Model" className="mb-6" />
      <p className="text-section-description max-w-4xl mx-auto text-center mb-12">
        From concept to commercial scale, our integrated approach covers every stage of lipid-based drug delivery development.
      </p>
      
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
        {capabilities.map((item, index) => (
          <SimpleReveal key={index} delay={index * 50}>
            <CapabilityCard item={item} />
          </SimpleReveal>
        ))}
      </ul>
    </section>
  );
};
